'use client';

import { Context } from './context';
import { usePopper } from './hooks';

import type { PopperContext } from './context';
import type { PopperProps } from '@mui/material/Popper';
import type { FC, ReactNode } from 'react';

export interface PopperProviderProps
  extends Partial<PopperContext>, Omit<Partial<PopperProps>, keyof PopperContext>
{
  isOpen?: boolean;
  anchorEl?: PopperProps['anchorEl'];
  children: ReactNode;
}

/**
 * provides context for controlling the Popover State
 * requires a <PopoverToggle> to consume the toggle handler
 * requires a <PopoverDisplay> to consume event handler
 *
 * @example
 * ```jsx
 *   <PopperProvider>
 *     <PopperToggle>
 *       ...
 *     </PopperToggle>
 *     <PopoverDisplay>
 *       ...
 *     </PopoverDisplay>
 *   </PopperProvider>
 * ```
 */

export const PopperProvider: FC<PopperProviderProps> = ({
  children,
  isOpen,
  anchorEl,
  ...props
}) => {
  const popper = usePopper( { id: 'popper', isOpen, anchorEl } );

  const value = {
    ...popper,
    ...props,
  };

  return (
    <Context.Provider value={value}>
      {children}
    </Context.Provider>
  );
};
